import React, { useEffect, useMemo, useState } from 'react'
import { useStoreState, useStoreActions } from 'store'
import { DisplayTab } from 'store/models/dimensions'
import { getOffersCounts } from 'utils/offers'
import SelectText from './SelectText'

const tabs = [
  {
    id: 'featured',
    label: 'Featured lenders',
  },
  {
    id: 'all',
    label: 'All results',
  },
]

const TabbedSection = () => {
  const [showTabs, setShowTabs] = useState(false)
  const [sendTrackStackedElement, setSendTrackStackedElement] = useState(true)
  const flags = useStoreState(state => state.featureFlags.flags)
  const { setActiveDisplayTab } = useStoreActions(actions => actions.dimensions)
  const { pristineOffers, isLoading, showingStacked } = useStoreState(state => state.offers)
  const { onClickTracking, onProductTracking, onViewedTracking } = useStoreActions(actions => actions.tracking)
  const { trackedEvents, correlationIds } = useStoreState(state => state.tracking)
  const activeDisplayTab = useStoreState(state => state.dimensions.activeDisplayTab)
  const isDesktop = useStoreState(state => state.dimensions.isDesktop)

  /**
   * Keeps track of the counts for featured and all offers, this value is
   * memoized until pristineOffers change
   */
  const offerCounts = useMemo(() => getOffersCounts(pristineOffers), [pristineOffers])

  /**
   * Updates the default tab to be featured offers when there are more than 3
   * featured offers. It also hides tabs if there are no
   * offers or the request is loading or there are no featured offers.
   */
  useEffect(() => {
    if (isLoading) {
      setActiveDisplayTab('all')
      return setShowTabs(!flags['allResultsHideTabs'] && !flags['showStackedOffers'])
    }

    if (
      !offerCounts.total ||
      !offerCounts.featured ||
      flags['allResultsHideTabs'] ||
      !isDesktop ||
      flags['showStackedOffers']
    ) {
      setActiveDisplayTab('all')
      onProductTracking({ eventName: 'ProductListViewed' })
      onProductTracking({ eventName: 'ProductAttributesCaptured' })
      trackStackedView()
      return setShowTabs(false)
    }

    if (offerCounts.featured >= 4) {
      setActiveDisplayTab('featured')
    }

    setShowTabs(true)
    onProductTracking({ eventName: 'ProductListViewed' })
    onProductTracking({ eventName: 'ProductAttributesCaptured' })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, offerCounts, setActiveDisplayTab, flags])

  const setActiveTab = (tabId: DisplayTab) => {
    if (tabId === activeDisplayTab) {
      return
    }
    setActiveDisplayTab(tabId)

    const [firstId, secondId] = correlationIds
    const currentCorrelationId = tabId === 'featured' ? firstId : secondId
    let tracked = false
    // check if most recent ProductListViewed has same currentCorrelationId
    // and for the right tab, else track it
    const events = trackedEvents.filter(
      e =>
        e.eventName === 'ProductListViewed' &&
        // @ts-ignore
        e.eventPayload.listName === (tabId === 'all' ? 'all products' : 'featured lenders')
    )
    if (events) {
      const lastEvent = events[events.length - 1]

      // @ts-ignore
      if (lastEvent && lastEvent.eventPayload.listId === currentCorrelationId) {
        tracked = true
      }
    }

    if (!tracked) {
      onProductTracking({ eventName: 'ProductListViewed' })
      onProductTracking({ eventName: 'ProductAttributesCaptured' })
    }
  }

  const clickHandler = (tabId: DisplayTab) => {
    onClickTracking({
      element: {
        type: 'Tab',
        text: tabId,
        position: 'top',
        location: 'rate table',
        name: 'Rate Table Filter',
      },
      outcome: tabId,
    })
    setActiveTab(tabId as DisplayTab)
  }

  const trackStackedView = () => {
    if (!sendTrackStackedElement) {
      return
    }

    if (flags['showStackedOffers'] && showingStacked) {
      onViewedTracking({
        element: {
          type: 'rate table',
          location: 'mortgage rate table',
          position: 'top',
          text: 'stacked rate table',
          name: 'Stacked Rate Table',
        },
      })
    } else if (flags['showStackedOffers'] && !showingStacked) {
      onViewedTracking({
        element: {
          type: 'rate table',
          location: 'mortgage rate table',
          position: 'top',
          text: 'stacked rate table all results',
          name: 'Stacked Rate Table All Results',
        },
      })
    }
    setSendTrackStackedElement(false)
  }

  return showTabs ? (
    <div data-tabs="tabs-container" className="tabs-container pos-relative" data-testid="tabs-container">
      <ul role="tablist" aria-label="tabs" data-tabs="tab-list" className="Tabs py-0 mb-0 md:mb-2">
        {tabs.map(tab => (
          <li key={`tabs-${tab.id}`} className="Tabs-item" role="presentation">
            <button
              role="tab"
              type="button"
              data-tabs="tab"
              data-testid={`tab-${tab.id}`}
              aria-selected="true"
              onClick={() => clickHandler(tab.id as DisplayTab)}
              className={`Tabs-link ${tab.id === activeDisplayTab && !isLoading ? 'is-active' : ''}`}
            >
              {tab.label} {!isLoading && `(${offerCounts[tab.id === 'all' ? 'total' : 'featured']})`}
            </button>
          </li>
        ))}
      </ul>

      <SelectText />

      {tabs.map(tab => (
        <div
          role="tabpanel"
          data-tabs="tab-panel"
          key={`content-${tab.id}`}
          data-testid={`content-${tab.id}`}
          className="Tabs-panel p-0"
          aria-hidden={tab.id !== activeDisplayTab}
        />
      ))}
    </div>
  ) : (
    <></>
  )
}

export default TabbedSection
